import { Group } from '@/components/_common';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';
import EyeIcon from '@/components/_common/EyeIcon';
import StatusTag from '@/components/_common/StatusTag';
import { WorkOrderMigrateConsultItem } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import {
  workorderTypeColor,
  workorderTypeItemSmall,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useDisclosure } from '@/hooks';
import { quantityFormatter } from '@/utils/number';
import formatterPercent from '@/utils/number/formatterPercent';
import formatterPrice from '@/utils/number/formatterPrice';
import { COLUMNS_WIDTH } from '@/utils/table';
import { Card, Table, Tooltip } from 'antd';
import { useState } from 'react';
import { Info } from 'react-feather';
import { useIntl } from 'react-intl';
import MigrationConsultItemDetailModal from './ItemDetailModal';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

interface Props {
  isItemRejectedByCustomer?: boolean;
  workOrderItems?: WorkOrderMigrateConsultItem[];
  loading: boolean;
}

export default function MigrationConsultItemsList(props: Props) {
  const intl = useIntl();

  const [itemSelected, setItemSelected] =
    useState<WorkOrderMigrateConsultItem>();

  const [opened, action] = useDisclosure(false, {
    onClose: () => setItemSelected(undefined),
  });

  const title = props.isItemRejectedByCustomer ? (
    <Group gap={10}>
      <FaTimesCircle size={16} color="red" />
      <span>Itens rejeitado pelo cliente</span>
    </Group>
  ) : (
    <Group gap={10}>
      <FaCheckCircle size={16} color="green" />
      <span>Itens autorizados</span>
    </Group>
  );

  const data = props.workOrderItems?.filter((item) =>
    props.isItemRejectedByCustomer
      ? item.isItemRejectedByCustomer
      : !item.isItemRejectedByCustomer,
  );

  return (
    <>
      <Card style={{ borderRadius: 8 }} loading={props.loading} title={title}>
        <Table
          loading={props.loading}
          scroll={{ x: true }}
          pagination={false}
          dataSource={data}
          columns={[
            {
              dataIndex: 'type',
              title: intl.formatMessage({
                id: 'workOrders.domain.items.type',
              }),
              width: 150,
              ellipsis: true,
              render: (_, item) => (
                <StatusTag
                  color={workorderTypeColor[item.type]}
                  label={workorderTypeItemSmall[item.type]}
                />
              ),
            },
            {
              dataIndex: 'itemDescription',
              title: intl.formatMessage({
                id: 'workOrders.domain.items.description',
              }),
              width: 200,
              render: (value) => (
                <EllipsisHoverToggle>{value}</EllipsisHoverToggle>
              ),
            },
            {
              dataIndex: 'quantity',
              width: COLUMNS_WIDTH.extraSmall,
              ellipsis: true,
              title: intl.formatMessage({
                id: 'workOrders.domain.items.quantityTime',
              }),
              align: 'right',
              render: (value: number) => (
                <span>{value ? quantityFormatter(value) : '-'}</span>
              ),
            },
            {
              dataIndex: 'unitPrice',
              title: intl.formatMessage({
                id: 'workOrders.domain.items.unitPrice',
              }),
              width: COLUMNS_WIDTH.price,
              ellipsis: true,
              align: 'right',
              render: (value: number) => <span>{formatterPrice(value)}</span>,
            },
            {
              dataIndex: 'discount',
              title: intl.formatMessage({
                id: 'workOrders.domain.items.discount',
              }),
              width: COLUMNS_WIDTH.price,
              ellipsis: true,
              align: 'right',
              render: (value: number) => {
                const discount = value / 100;

                return (
                  <Group gap={4} position="right">
                    <span>{formatterPercent(discount)}</span>

                    <Tooltip title={String(value).replace('.', ',') + '%'}>
                      <Info size={14} />
                    </Tooltip>
                  </Group>
                );
              },
            },
            {
              title: intl.formatMessage({
                id: 'workOrders.domain.items.discountValue',
              }),
              align: 'right',
              width: COLUMNS_WIDTH.price,
              ellipsis: true,
              render: (_, item) => {
                const discount = item.discount / 100;
                return (
                  <span>
                    {formatterPrice(item.quantity * item.unitPrice * discount)}
                  </span>
                );
              },
            },
            {
              title: intl.formatMessage({
                id: 'workOrders.domain.items.totalValue',
              }),
              align: 'right',
              width: COLUMNS_WIDTH.price,
              ellipsis: true,
              render: (_, item) => {
                const discount = item.discount / 100;
                return (
                  <span>
                    {formatterPrice(
                      item.quantity * item.unitPrice -
                        item.quantity * item.unitPrice * discount,
                    )}
                  </span>
                );
              },
            },
            {
              title: intl.formatMessage({
                id: 'commons.list.header.action',
              }),
              width: 150,
              align: 'right',
              fixed: 'right',
              render: (_, item) => (
                <EyeIcon
                  iconProps={{
                    onClick: () => {
                      setItemSelected(item);
                      action.open();
                    },
                  }}
                />
              ),
            },
          ]}
        />
      </Card>

      <MigrationConsultItemDetailModal
        item={itemSelected}
        opened={opened}
        onClose={action.close}
      />
    </>
  );
}
